import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { UserProfiles } from '../../services';
import { Profile } from '../../services/Contexture/Profile';

const initialState = {
  userProfile: null,
  applicationProfile: null,
  isFetching: false,
  error: null,
};

// Fetch userProfile (techStack/tools) and applicationProfile using auth.user and auth.profiles
export const fetchProfilesMeta = createAsyncThunk(
  'profiles/fetchProfilesMeta',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { user, profiles } = state.auth;

      if (!user || !profiles) {
        return rejectWithValue('Missing auth user or profiles for profiles meta');
      }

      const userId = user.profile?.['cognito:username'];
      let userProfileData = null;

      if (userId) {
        try {
          const profileResponse = await UserProfiles.fetchByUserId(userId);
          if (profileResponse?.data) {
            userProfileData = profileResponse.data;
          }
        } catch (error) {
          console.error('Error fetching application profile:', error);
        }
      }

      // Fetch tech stack and tools from contexture profiles
      let techStack = null;
      let tools = null;

      try {
        if (profiles.stack_news?.profileId) {
          const { terms: techStackTerms } = await Profile.fetchCustomTerms({
            profileId: profiles.stack_news.profileId,
          });
          techStack = techStackTerms.join(',');
        }

        if (profiles.tools_news?.profileId) {
          const { terms: toolsTerms } = await Profile.fetchCustomTerms({
            profileId: profiles.tools_news.profileId,
          });
          tools = toolsTerms.join(',');
        }
      } catch (error) {
        console.error('Error fetching tech stack/tools:', error);
      }

      const applicationProfileData = {
        ...(userProfileData || {}),
        profiles,
        ...(techStack && { techStack }),
        ...(tools && { tools }),
      };

      return {
        userProfile: { techStack, tools },
        applicationProfile: userId ? applicationProfileData : profiles ? { profiles } : null,
      };
    } catch (error) {
      console.error('Error in fetchProfilesMeta thunk:', error);
      return rejectWithValue(error.message || 'Failed to fetch profiles metadata');
    }
  }
);

const profilesSlice = createSlice({
  name: 'profiles',
  initialState,
  reducers: {
    setUserProfile(state, action) {
      state.userProfile = action.payload;
    },
    setApplicationProfile(state, action) {
      state.applicationProfile = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfilesMeta.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(fetchProfilesMeta.fulfilled, (state, action) => {
        state.isFetching = false;
        state.userProfile = action.payload.userProfile;
        state.applicationProfile = action.payload.applicationProfile;
      })
      .addCase(fetchProfilesMeta.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload || action.error.message;
        state.userProfile = null;
        state.applicationProfile = null;
      });
  },
});

export const { setUserProfile, setApplicationProfile } = profilesSlice.actions;

export default profilesSlice.reducer;
